import { useCallback, useState } from "react";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";

import { DEFAULT_ACCOMMODATION_HOTEL_POLICY_INPUT_MODEL } from "./constants/default-input-model";
import { excludeKeys, type AccommodationHotelPolicyFormModel, type ExcludeKeys } from "./type";
import { hotelPolicyInputZodSchema, type HotelPolicyZodSchemaType } from "./validation-schema";

import type { AccommodationHotelPolicyFormModelContextType } from "../ui/context";

type Props = {
	inputModel: AccommodationHotelPolicyFormModel["InputModel"] | undefined;

	queryData: AccommodationHotelPolicyFormModel["QueryData"];
};

export const useInitAccommodationHotelPolicyFormModelContext = ({
	inputModel: _inputModel,
	queryData,
}: Props): Pick<AccommodationHotelPolicyFormModelContextType, "viewModel" | "onSetViewModel"> & {
	onResetViewModel: () => void;
} => {
	const defaultInputModel = DEFAULT_ACCOMMODATION_HOTEL_POLICY_INPUT_MODEL({ queryData });
	const inputModel = _inputModel ?? defaultInputModel;

	const [viewModel, setViewModel] = useState(
		inputModelToViewModelConverter<
			AccommodationHotelPolicyFormModel["InputModel"],
			ExcludeKeys,
			AccommodationHotelPolicyFormModel["QueryData"],
			HotelPolicyZodSchemaType
		>({
			defaultInput: defaultInputModel,
			initInput: inputModel,
			currentInput: inputModel,
			excludeKeys: excludeKeys,
			zodSchema: hotelPolicyInputZodSchema,
		}),
	);

	const onSetViewModel: AccommodationHotelPolicyFormModelContextType["onSetViewModel"] = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce(
					(acc, cur) => {
						acc[cur.key] = cur.value;
						return acc;
					},
					{} as Partial<AccommodationHotelPolicyFormModel["ViewModel"]>,
				),
			};

			const currentInput = viewModelToInputModelConverter<
				AccommodationHotelPolicyFormModel["InputModel"],
				ExcludeKeys,
				AccommodationHotelPolicyFormModel["QueryData"]
			>(mergedViewModel);

			const newViewModel = inputModelToViewModelConverter<
				AccommodationHotelPolicyFormModel["InputModel"],
				ExcludeKeys,
				AccommodationHotelPolicyFormModel["QueryData"],
				HotelPolicyZodSchemaType
			>({
				defaultInput: defaultInputModel,
				initInput: inputModel,
				currentInput,
				excludeKeys: excludeKeys,
				zodSchema: hotelPolicyInputZodSchema,
			});

			setViewModel(newViewModel);
		},
		[defaultInputModel, inputModel, viewModel],
	);

	const onResetViewModel = useCallback(() => {
		setViewModel((prev) => ({
			...prev,
			isDirty: false,
		}));
	}, []);

	return {
		viewModel,
		onSetViewModel,
		onResetViewModel,
	};
};
